import { ExternalLink } from 'lucide-react'
import { usePreviewStore } from '@/stores/preview-store'
import {
  displayCitationPage,
  resolveCitationTargetPage,
  toPrecisePreviewHighlight,
} from '@/lib/citation-preview'
import type { SourceHighlightData } from '@/stores/assistant-store'

interface CitationLinkProps {
  page: number | null
  documentId: string | null
  documentName?: string | null
  sourceQuote?: string | null
  /**
   * Highlight data captured at extraction time. V1 (PDF rects) targets the page
   * and acts as a fallback; when `sourceQuote` is present it drives a precise
   * text-layer highlight of the cited sentence (quote-first, spec Part 2).
   */
  sourceHighlightData?: SourceHighlightData | null
  /** Manual quality tier (PR 7). 'gold' shows a star inline. */
  qualityTier?: string | null
  /**
   * Which surface owns the preview this citation opens. Defaults to 'analysis'
   * because every original caller is an Analysis view; the Submission Pack
   * passes 'pack' so its page-level gate (D-12) shows and cleans up the modal.
   */
  context?: 'library' | 'chat' | 'analysis' | 'pack'
}

export function CitationLink({
  page,
  documentId,
  documentName,
  sourceQuote,
  sourceHighlightData,
  context = 'analysis',
}: CitationLinkProps) {
  const openPreview = usePreviewStore((state) => state.openPreview)
  const shownPage = displayCitationPage(page)

  if (!page && !documentId && !documentName && !sourceQuote) {
    return <span className="text-muted-foreground">—</span>
  }

  if (!documentId) {
    if (shownPage != null) {
      return (
        <span
          className="text-meta"
          title={documentName ?? sourceQuote ?? `Page ${shownPage}`}
        >
          {documentName ? `${documentName} · p.${shownPage}` : `p.${shownPage}`}
        </span>
      )
    }

    if (documentName) {
      return (
        <span className="text-meta truncate" title={documentName}>
          {documentName}
        </span>
      )
    }

    return <span className="text-meta">Quote</span>
  }

  const handleClick = () => {
    // Quote-first: the verbatim source_quote drives the visible highlight;
    // V1 chunk rects are kept for page targeting and as a fallback when the
    // text-layer search misses (scanned/rotated PDFs).
    const precise = toPrecisePreviewHighlight(
      sourceHighlightData,
      sourceQuote,
      `extraction-${documentId}-${page ?? 'none'}`,
    )
    const targetPage = resolveCitationTargetPage(
      precise?.highlight ? { highlight: precise.highlight } : undefined,
      page,
    )

    void openPreview({
      documentId,
      // Defaults to 'analysis': this component is rendered by the Analysis
      // extraction views (compliance matrix, risk register, key dates,
      // evaluation criteria, submission checklist), plus ExtractionTable
      // (new-items banner / removed-items pile) and RequirementDrawer — all
      // Analysis surfaces. The Submission Pack drawer overrides it to 'pack'.
      context,
      ...(targetPage != null ? { page: targetPage } : {}),
      ...(precise?.highlight ? { highlight: precise.highlight } : {}),
      ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
    })
  }

  return (
    <button
      onClick={handleClick}
      className="text-meta hover:text-foreground flex items-center gap-1 transition-colors"
      title={
        sourceQuote
          ? `"${sourceQuote.slice(0, 100)}..."`
          : shownPage != null
            ? `Page ${shownPage}`
            : (documentName ?? undefined)
      }
    >
      {shownPage != null ? `p.${shownPage}` : (documentName ?? 'Source')}
      <ExternalLink className="h-3 w-3" />
    </button>
  )
}
